import { marketingConsentGranted } from '@/lib/marketingConsent';

// Google (GA4 + Google Ads) event tracking for the onboarding → purchase funnel.
//
// The gtag stub is defined in index.html <head> before any tracker loads, so
// window.gtag is always callable — early events queue in dataLayer until the
// GA4 script arrives.
//
// Funnel model (each step is a GA4 event; mark the important ones as key
// events in GA4 Admin → Events, then import them into Google Ads):
//   funnel_01_home_view          vovy.ai/
//   funnel_02_onboarding_view    /onboard/welcome (new auth flow)
//   onboarding_started/step/completed  (mirrored from Mixpanel, per-step)
//   funnel_03_signup_view        /signup, /signin
//   funnel_04_paywall_view       /plans/start
//   funnel_05_checkout_view      /learn/checkout, /app/learn/checkout
//   begin_checkout               Stripe embedded session created
//   purchase                     webhook-confirmed subscription (value + plan)
//   funnel_06_first_unit_view    /app/learn/intro-to-vibe-coding/*
//   lesson_started / lesson_completed  (mirrored from Learn analytics)

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

type GaParams = Record<string, string | number | boolean | undefined | unknown>;

/** GA4 custom-param values must be primitives; drop anything else. */
const sanitizeParams = (params?: Record<string, unknown>): GaParams => {
  const out: GaParams = {};
  if (!params) return out;
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      out[key] = value;
    }
  }
  return out;
};

export const gaEvent = (name: string, params?: Record<string, unknown>) => {
  try {
    if (typeof window === 'undefined') return;
    if (window.gtag) {
      window.gtag('event', name, sanitizeParams(params));
    } else {
      (window.dataLayer = window.dataLayer || []).push([
        'event',
        name,
        sanitizeParams(params),
      ]);
    }
  } catch {
    /* analytics must never break the app */
  }
};

// ── GTM dataLayer events ─────────────────────────────────────────────────────
// The GTM container (GTM-PD3TC2JV, published as "New onboarding flow sync")
// fans these custom events out to Google Ads, GA4 event tags, Facebook Pixel,
// LinkedIn, and X. GTM owns ALL ad-platform conversions — do not also fire
// gtag send_to conversions here or Google Ads double-counts. That also means
// no ad-platform base pixel belongs in the app: LinkedIn, X, Facebook and the
// Google tags are all injected BY the container.
//
// This union is the complete, authoritative surface. Every name here needs a
// matching custom-event trigger + tag in the GTM UI; adding a name to the union
// without wiring GTM produces a silently-dropped event.
export type DataLayerEvent =
  /**
   * Signup method breakdown — **GA4 ONLY. NOT an ad-platform conversion.**
   *
   * Fires on the same trigger as `free_account_success` ('Sign Up Completed'),
   * so mapping BOTH to a Google Ads conversion double-counts every signup and
   * splits the optimization signal across two actions. `free_account_success`
   * won that arbitration (see below); this event is retired from ALL
   * ad-platform mapping — Google Ads, Meta, LinkedIn and X.
   *
   * It is kept, not deleted, because it is the only signup push carrying
   * `method` (email | google | github | apple), which GA4 uses for the
   * signup-source breakdown. Do not attach a Google Ads / Meta / LinkedIn / X
   * tag to its trigger. See .claude/GTM-RUNBOOK.md §2b.
   */
  | 'email_captured'
  /**
   * Account created — the SINGLE signup conversion for every ad platform.
   * Google Ads **primary** ("Free account"), Meta, and (planned) LinkedIn + X.
   * Guarded, see trackFreeAccountCreated.
   */
  | 'free_account_success'
  /** Monthly Purchase (GAds/GA4/FB) + Purchase (LinkedIn/X). Carries value+tier. */
  | 'monthly_purchase'
  /** Yearly Purchase (GAds/GA4/FB) + Purchase (LinkedIn/X). Carries value+tier. */
  | 'yearly_purchase'
  /** Plan-agnostic purchase completion (Learn + desktop). */
  | 'purchase_complete'
  /** Checkout session created, payment not yet made (Learn + desktop). */
  | 'purchase_started'
  /** Checkout session created but abandoned without paying (Learn). */
  | 'abandoned_cart'
  /** macOS DMG download CTA clicked. */
  | 'desktop_download_click'
  /** Home hero "start learning" CTA clicked. */
  | 'hero_start_learning_click'
  /** Vovy-for-Windows waitlist email captured. NOT an account — never map this
   *  to the "Account created" conversion or it inflates signups. */
  | 'windows_waitlist_joined'
  /** "Email me the Mac link" captured (mobile/off-platform download CTA). Same
   *  rule as the waitlist: an email is NOT an account — never map to the
   *  "Account created" conversion. */
  | 'mac_link_requested';

export const pushDataLayerEvent = (
  event: DataLayerEvent,
  params?: Record<string, unknown>
) => {
  try {
    if (typeof window === 'undefined') return;
    (window.dataLayer = window.dataLayer || []).push({ event, ...sanitizeParams(params) });
  } catch {
    /* analytics must never break the app */
  }
};

/**
 * GA4 ecommerce purchase — dedupes on transaction_id.
 *
 * `plan` is the BILLING PERIOD (monthly | yearly) and selects the GTM event.
 * `tier` is the PRODUCT (pro | max | learn) and is what carries the real price:
 * monthly is $30 on Pro but $100 on Max, yearly is $240 on Pro but $960 on Max.
 * The monthly_purchase / yearly_purchase tags in GTM MUST read `value` from the
 * dataLayer ({{DLV - value}}) — a tag with a hardcoded $30/$240 will misreport
 * every Max sale.
 */
const reportedPurchases = new Set<string>();
const PURCHASE_RECEIPTS_KEY = 'vovy_purchase_receipt_v1:';
const ADS_RECEIPTS_KEY = 'vovy_purchase_ads_dispatch_v1:';
const ADS_DEFERRED_KEY = 'vovy_purchase_ads_deferred_v1:';
const reportedAds = new Set<string>();
const deferredAdPurchases = new Set<string>();
function purchaseDiagnostic(stage: string) {
  if (typeof window !== 'undefined' && new URLSearchParams(window.location?.search || '').get('tracking_debug') === '1')
    console.info('[purchase-tracking]', stage);
}
const GA4_RECEIPTS_KEY = 'vovy_ga4_purchase_dispatch_v2:';
function purchaseItems(p: {value:number; plan:string; tier?:string}) {
  return [{item_name: ['Vovy', p.tier, p.plan].filter(Boolean).join(' '),
    item_brand: 'Vovy', item_category: 'Software', price: p.value, quantity: 1}];
}
/** Keep GA4 dispatch independent of the legacy all-channel queue marker.
 * A callback confirms tag processing, not receipt in Google's reporting UI.
 * Unacknowledged queues can retry after five minutes with the same transaction ID.
 */
function dispatchGa4Purchase(p: {value:number; currency?:string; transactionId:string; plan:string; tier?:string; surface?:'desktop'|'web'}) {
  if (typeof window === 'undefined') return;
  const recovery = new URLSearchParams(window.location?.search || '').get('ga4_recover') === '1';
  if (!recovery && !window.gtag) { purchaseDiagnostic('google_tag_unavailable'); return; }
  const key = (recovery ? 'vovy_ga4_purchase_recovery_v6:' : GA4_RECEIPTS_KEY) + p.transactionId;
  try {
    const previous = localStorage.getItem(key);
    if (previous === 'dispatched' || (previous && Date.now() - Number(previous) < 300_000)) { purchaseDiagnostic(previous === 'dispatched' ? 'ga4_previously_dispatched' : 'ga4_dispatch_pending'); return; }
    localStorage.setItem(key, String(Date.now()));
  } catch { /* Browser storage is optional. */ }
  try {
    purchaseDiagnostic('ga4_purchase_queued');
    if (recovery) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'ga4_receipt_recovery', transaction_id: p.transactionId,
        value: p.value, currency: p.currency ?? 'USD', surface: p.surface ?? 'web', ecommerce: {items: purchaseItems(p)},
        eventCallback: () => { purchaseDiagnostic('ga4_recovery_tag_callback'); try { localStorage.setItem(key, 'dispatched'); } catch {} },
      });
      return;
    }
    window.gtag('event', 'purchase', {
      send_to: 'G-9CW44KB6BD', value:p.value, currency:p.currency ?? 'USD',
      transaction_id:p.transactionId, event_id:p.transactionId, plan:p.plan,
      tier:p.tier, surface:p.surface ?? 'web', items: purchaseItems(p),
      event_callback: () => { purchaseDiagnostic('ga4_tag_callback'); try { localStorage.setItem(key, 'dispatched'); } catch {} },
    });
  } catch { try { localStorage.setItem(key, '0'); } catch {} }
}

export const gaPurchase = (p: {
  value: number;
  currency?: string;
  transactionId: string;
  plan: string;
  tier?: string;
  surface?: 'desktop' | 'web';
  marketingConsent?: boolean;
  ga4Delivery?: 'server';
}): boolean => {
  if (!/^cs_[A-Za-z0-9_]{5,200}$/.test(p.transactionId) || !Number.isFinite(p.value) || p.value < 0) return false;
  if (p.ga4Delivery !== 'server') dispatchGa4Purchase(p);
  const receiptKey = PURCHASE_RECEIPTS_KEY + p.transactionId;
  let previousReceipt = reportedPurchases.has(p.transactionId);
  let previousAds = reportedAds.has(p.transactionId);
  let deferredAds = deferredAdPurchases.has(p.transactionId);
  try {
    previousReceipt ||= !!localStorage.getItem(receiptKey);
    previousAds ||= !!localStorage.getItem(ADS_RECEIPTS_KEY + p.transactionId);
    deferredAds ||= !!localStorage.getItem(ADS_DEFERRED_KEY + p.transactionId);
  } catch { /* In-memory dedupe still works when storage is unavailable. */ }
  // A checkout's consent snapshot alone cannot override the visitor's current choice.
  const adsEligible = p.marketingConsent === true && marketingConsentGranted() && ['monthly', 'yearly'].includes(p.plan);
  // An earlier analytics callback can race the native consent handoff. Only a
  // recorded deferred state may retry ads; legacy purchase markers stay intact.
  if (adsEligible && !previousAds && (!previousReceipt || deferredAds)) {
    // This verified ecommerce object intentionally preserves its structured items.
    // Generic custom-event sanitization accepts only primitive values.
    try { if (typeof window !== 'undefined') { (window.dataLayer = window.dataLayer || []).push({
      event: p.plan === 'yearly' ? 'yearly_purchase' : 'monthly_purchase',
      value: p.value, currency: p.currency ?? 'USD', transaction_id: p.transactionId,
      event_id: p.transactionId, tier: p.tier, surface: p.surface ?? 'web', ecommerce: {items: purchaseItems(p)},
    });
      reportedAds.add(p.transactionId);
      try { localStorage.setItem(ADS_RECEIPTS_KEY + p.transactionId, 'queued'); } catch {}
    } } catch { /* Analytics must not interrupt checkout. */ }
  } else if (!adsEligible && !previousReceipt) {
    deferredAdPurchases.add(p.transactionId);
    try { localStorage.setItem(ADS_DEFERRED_KEY + p.transactionId, '1'); } catch {}
  }
  if (previousReceipt) return false;
  reportedPurchases.add(p.transactionId);
  try { localStorage.setItem(receiptKey, '1'); }
  catch { /* No storage: this page still reports once; GA receives the real transaction id. */ }
  return true;
};

// ── "Free account": THE signup conversion ────────────────────────────────────
// free_account_success is the ONLY signup event mapped to an ad platform, and
// the Google Ads PRIMARY conversion. `email_captured` fires on the same trigger
// and is deliberately GA4-only — see its JSDoc in the union above and
// .claude/GTM-RUNBOOK.md §2b for the UI work that enforces it.
//
// It used to have exactly one producer: completing the Learn onboarding flow
// (src/pages/NewAuthFlow.tsx:1306 → onboarding_completed). Desktop-acquired
// users never walk that flow, so the primary conversion never fired for what is
// now the primary product and Google Ads was optimizing on a signal most new
// users never emit.
//
// It now fires on ACCOUNT CREATION, which every surface reaches (SignUpForm /
// useAuthService email signup, AuthCallback SSO — all funnel through
// trackAuthEvents.signUpCompleted → 'Sign Up Completed' below). Learn
// onboarding is kept as a LEGACY FALLBACK for accounts created before this
// change, gated so it can never add a second conversion for the same account.
//
// ── Guard model (two keys, different lifetimes) ──
// The old guard was a single permanent key, which fixed double-firing but
// introduced a silent conversion-suppressing bug: a genuinely different human
// signing up in the same browser (shared machine, a founder demoing signup, a
// second family member) emitted NOTHING, forever. Two keys instead:
//
//   FREE_ACCOUNT_FIRED_KEY  — the dedup guard. Set on fire; CLEARED ON SIGN-OUT
//     (mirrorEventToGA below). Sign-out is the only signal available at this
//     layer that the browser may be about to serve a different person, and a
//     different person must sign out the previous session before signing up.
//   FREE_ACCOUNT_EVER_KEY   — permanent, never cleared. Gates ONLY the Learn
//     onboarding fallback producer, so signup → sign out → sign in → finish
//     onboarding cannot re-arm the guard and double-count one account.
//
// Residual (accepted, documented): signup → sign out → a second signup attempt
// with the SAME already-registered email fires a second conversion, because
// supabase.auth.signUp returns a truthy obfuscated user for existing emails
// (src/components/auth/SignUpForm.tsx:126). Rare, and strictly preferable to
// silently dropping real signups. Google Ads' per-action "Count: One" setting
// absorbs it whenever both land on the same ad click.
const FREE_ACCOUNT_FIRED_KEY = 'vovy_ga_free_account_success';
const FREE_ACCOUNT_EVER_KEY = 'vovy_ga_free_account_ever';

const hasFiredFreeAccountEver = () => {
  try {
    return !!localStorage.getItem(FREE_ACCOUNT_EVER_KEY);
  } catch {
    return false;
  }
};

/**
 * Fire the "Free account" conversion at most once per signed-in identity.
 * Cleared by clearFreeAccountGuard() on sign-out so a different user on the
 * same browser is still counted.
 */
export const trackFreeAccountCreated = (params?: Record<string, unknown>) => {
  try {
    if (typeof window === 'undefined') return;
    try {
      if (localStorage.getItem(FREE_ACCOUNT_FIRED_KEY)) return;
      const now = new Date().toISOString();
      localStorage.setItem(FREE_ACCOUNT_FIRED_KEY, now);
      localStorage.setItem(FREE_ACCOUNT_EVER_KEY, now);
    } catch {
      /* storage blocked → fire anyway rather than lose the conversion */
    }
    pushDataLayerEvent('free_account_success', params);
  } catch {
    /* analytics must never break the app */
  }
};

/**
 * Re-arm the guard on sign-out. The permanent EVER key is deliberately NOT
 * cleared — it is what stops the Learn-onboarding fallback double-counting.
 */
const clearFreeAccountGuard = () => {
  try {
    localStorage.removeItem(FREE_ACCOUNT_FIRED_KEY);
  } catch {
    /* storage blocked → nothing to clear */
  }
};

// ── Mixpanel event mirror ────────────────────────────────────────────────────
// trackEvent() (src/utils/mixpanel.ts) forwards these events to GA4 so the
// whole onboarding stepper and Learn taxonomy stay in one implementation.
const MIXPANEL_TO_GA: Record<string, string> = {
  onboarding_started: 'onboarding_started',
  onboarding_step_viewed: 'onboarding_step_viewed',
  onboarding_step_completed: 'onboarding_step_completed',
  onboarding_answer: 'onboarding_answer',
  onboarding_completed: 'onboarding_completed',
  onboarding_exit: 'onboarding_exit',
  'Sign Up Completed': 'sign_up',
  'Lesson Started': 'lesson_started',
  'Lesson Completed': 'lesson_completed',
  'Path Opened': 'path_opened',
};

export const mirrorEventToGA = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  const gaName = MIXPANEL_TO_GA[eventName];
  if (gaName) gaEvent(gaName, properties);
  // GTM events. Exactly ONE signup conversion reaches the ad platforms:
  // free_account_success. email_captured is pushed alongside it for GA4's
  // signup-method breakdown and must have NO ad-platform tag on its trigger.
  if (eventName === 'Sign Up Completed') {
    pushDataLayerEvent('email_captured', {
      method: typeof properties?.method === 'string' ? properties.method : undefined,
    });
    // Account creation IS the free account — fires for desktop signups too,
    // which never reach the Learn onboarding flow.
    trackFreeAccountCreated({ trigger: 'signup_completed' });
  }
  // Legacy fallback: only for accounts created BEFORE account-creation became
  // the producer. hasFiredFreeAccountEver() makes this a no-op for anyone whose
  // signup already reported, even across a sign-out that re-armed the guard.
  if (
    eventName === 'onboarding_completed' &&
    properties?.signed_in &&
    !hasFiredFreeAccountEver()
  ) {
    trackFreeAccountCreated({ trigger: 'learn_onboarding_completed' });
  }
  // Sign-out re-arms the guard so a different human on this browser still
  // reports a signup. See the guard model above.
  if (eventName === 'Sign Out') clearFreeAccountGuard();
};

// ── Route-based funnel milestones (fired by RouteTracker) ───────────────────
export const trackFunnelPage = (pathname: string) => {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/') return gaEvent('funnel_01_home_view', { path: p });
  if (p.startsWith('/onboard') || p === '/newauthflow' || p === '/vovy/demo')
    return gaEvent('funnel_02_onboarding_view', { path: p });
  if (p === '/signup' || p === '/signin')
    return gaEvent('funnel_03_signup_view', { path: p });
  if (p === '/plans/start') return gaEvent('funnel_04_paywall_view', { path: p });
  if (p.endsWith('/checkout') || p.includes('/checkout?'))
    return gaEvent('funnel_05_checkout_view', { path: p });
  if (p.startsWith('/app/learn/intro-to-vibe-coding'))
    return gaEvent('funnel_06_first_unit_view', { path: p });
};
